import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Badge } from '@/components/admin/ui/badge';
import { Input } from '@/components/admin/ui/input';
import { Button } from '@/components/admin/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { Loader2, Search } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { classifyBillingQueues, queueTotalCents, monitorExposure, QUEUES } from '@/lib/billing/queues';
import { fetchInBatches } from '@utils/batchSupabaseQueries';
import toast from 'react-hot-toast';
import { consoleLink } from '@/lib/staging/consoleLink';

/**
 * Billing workbench (read-only release, Sep 2026).
 *
 * Kendra's starting page: every queue is computed from the booking_balances view, never from a
 * stored balance. The morning report and webhook queue health come from /api/admin/billing/summary.
 * Actions (create invoice, finalize, write-off, refund) arrive in the next release; until then the
 * client page's Add Payment / Add Refund remain the way to record money.
 */

const dollars = (cents: number | null | undefined) =>
  '$' + (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortDate = (d?: string | null) => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '-');
const mountainToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Denver', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const daysBetween = (a: string, b: string) => Math.round((Date.parse(b + 'T12:00:00Z') - Date.parse(a + 'T12:00:00Z')) / 86400000);

const planLabel: Record<string, string> = { monthly99: '$99 / month', monthly199: '$199 / month', deposit500: '$500 deposit', full: 'paid in full' };

async function authedFetch(path: string, init: RequestInit = {}) {
  const { data: { session } } = await supabaseAdmin.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const res = await fetch(path, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${session.access_token}` } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

const BillingWorkbench = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [users, setUsers] = useState<Map<string, any>>(new Map());
  const [summary, setSummary] = useState<any>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [runningReport, setRunningReport] = useState(false);
  // Final-invoice drafts brought to what clients still owe (22 Oct 2026: early payments never reached the drafts)
  const [syncingDrafts, setSyncingDrafts] = useState(false);
  const [draftSync, setDraftSync] = useState<any | null>(null);
  const [requeueing, setRequeueing] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeQueue, setActiveQueue] = useState<string>('overdue');
  const [search, setSearch] = useState('');
  const today = mountainToday();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all: any[] = [];
        for (let from = 0; ; from += 1000) {
          const { data, error } = await supabaseAdmin
            .from('booking_balances')
            .select('booking_id, customer_id, booking_status, is_active, is_placeholder, is_comped, needs_backfill, payment_plan, subscription_status, event_date, contract_cents, paid_cents, remaining_cents, overpaid_cents, final_due_date, due_date_set, stripe_final_invoice_id, final_invoice_finalized, stripe_subscription_id, stripe_customer_id, last_paid_at')
            .range(from, from + 999);
          if (error) throw error;
          all.push(...(data || []));
          if (!data || data.length < 1000) break;
        }
        const active = all.filter((r) => r.is_active && !r.is_placeholder);
        const ids = [...new Set(active.map((r) => r.customer_id).filter(Boolean))];
        const { data: userRows } = await fetchInBatches(supabaseAdmin, 'users', 'id, full_name, email, phone, sms_transactional_consent, sms_opted_out', 'id', ids, { batchSize: 200 });
        if (cancelled) return;
        setUsers(new Map((userRows || []).map((u: any) => [u.id, u])));
        setRows(active);
      } catch (err: any) {
        if (!cancelled) setLoadError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    authedFetch('/api/admin/billing/summary').then((s) => !cancelled && setSummary(s)).catch((e) => !cancelled && setSummaryError(e.message));
    return () => { cancelled = true; };
  }, []);

  const { byQueue, rowsWithQueues } = useMemo(() => classifyBillingQueues(rows, today), [rows, today]);

  const searchTerm = search.trim().toLowerCase();
  const visible = useMemo(() => {
    if (searchTerm) {
      return rowsWithQueues.filter((r) => {
        const u = users.get(r.customer_id) || {};
        return [u.full_name, u.email, u.phone].some((v) => String(v || '').toLowerCase().includes(searchTerm));
      });
    }
    return byQueue[activeQueue] || [];
  }, [searchTerm, rowsWithQueues, byQueue, activeQueue, users]);

  const invoiceLabel = (r: any) => (r.stripe_final_invoice_id ? (r.final_invoice_finalized ? 'finalized' : 'draft') : 'none');
  const cardLabel = (r: any) => (r.subscription_status === 'past_due' ? 'failing' : r.stripe_customer_id ? 'on file' : 'none');
  const textLabel = (u: any) => (u?.phone && u?.sms_transactional_consent && !u?.sms_opted_out ? 'yes' : 'no consent');

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Billing</h1>
          <p className="text-sm text-muted-foreground mt-1">Balances computed from payments, refunds, fees and the contract. Nothing here is a stored copy.</p>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search a client by name, email, or phone" className="pl-9" aria-label="Search clients" />
        </div>
      </div>

      {loadError && <p className="mb-4 text-sm text-destructive">Could not load balances: {loadError}</p>}

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        {QUEUES.map((q) => {
          const list = byQueue[q.key] || [];
          const isActive = !searchTerm && activeQueue === q.key;
          return (
            <button key={q.key} type="button" onClick={() => { setSearch(''); setActiveQueue(q.key); }}
              className={`rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? 'border-primary ring-1 ring-primary' : 'border-border'}`}
              aria-pressed={isActive}>
              <div className="text-sm font-medium text-muted-foreground">{q.title}</div>
              <div className="mt-1 text-3xl font-bold tabular-nums">{loading ? '…' : list.length}</div>
              <div className="text-xs text-muted-foreground">{loading ? q.hint : `${dollars(queueTotalCents(list, q.key))} · ${q.hint}`}</div>
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-lg">
              {searchTerm ? `Search: “${search.trim()}”` : QUEUES.find((q) => q.key === activeQueue)?.title}
              <span className="ml-3 text-sm font-normal text-muted-foreground">{visible.length} booking{visible.length === 1 ? '' : 's'} · {dollars(queueTotalCents(visible, searchTerm ? null : activeQueue))}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading balances…</div>
            ) : visible.length === 0 ? (
              <p className="py-10 text-center text-muted-foreground">{searchTerm ? 'No active booking matches.' : 'Nothing in this queue.'}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Card</TableHead>
                      <TableHead>Texts</TableHead>
                      <TableHead>Payment monitor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.map((r: any) => {
                      const u = users.get(r.customer_id) || {};
                      const exposure = monitorExposure(r, today);
                      const days = r.event_date ? daysBetween(today, String(r.event_date).slice(0, 10)) : null;
                      return (
                        <TableRow key={r.booking_id}>
                          <TableCell>
                            <Link to={consoleLink(`/lp-console/billing/${r.booking_id}`)} className="font-medium hover:text-primary hover:underline">{u.full_name || 'Unknown client'}</Link>
                            <div className="text-xs text-muted-foreground">
                              {planLabel[r.payment_plan] || r.payment_plan} · {r.booking_status}{r.subscription_status ? ` · sub ${r.subscription_status}` : ''}{r.is_comped ? ' · comped' : ''}
                              {searchTerm && r.queues?.length ? ` · ${r.queues.join(', ')}` : ''}
                            </div>
                          </TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">{shortDate(r.event_date)}<div className="text-xs text-muted-foreground">{days === null ? '' : days < 0 ? `${Math.abs(days)}d ago` : `in ${days}d`}</div></TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">{shortDate(r.final_due_date)}<div className="text-xs text-muted-foreground">{r.due_date_set ? 'set' : 'event − 2 mo'}</div></TableCell>
                          <TableCell className={`text-right tabular-nums font-semibold ${Number(r.overpaid_cents || 0) > 0 && Number(r.remaining_cents || 0) === 0 ? 'text-sky-700' : ''}`}>{Number(r.overpaid_cents || 0) > 0 && Number(r.remaining_cents || 0) === 0 ? `−${dollars(r.overpaid_cents)}` : dollars(r.remaining_cents)}<div className="text-xs font-normal text-muted-foreground">{dollars(r.paid_cents)} of {dollars(r.contract_cents)}</div></TableCell>
                          <TableCell><Badge variant="outline" className={invoiceLabel(r) === 'none' ? 'border-red-300 bg-red-50 text-red-700' : ''}>{invoiceLabel(r)}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className={cardLabel(r) === 'failing' ? 'border-red-300 bg-red-50 text-red-700' : ''}>{cardLabel(r)}</Badge></TableCell>
                          <TableCell className="text-sm">{textLabel(u) === 'yes' ? <span className="text-emerald-700">yes</span> : <span className="text-amber-700">{textLabel(u)}</span>}</TableCell>
                          <TableCell className="text-xs">{exposure ? <span className={exposure.kind === 'cancel' ? 'font-semibold text-destructive' : 'text-amber-700'}>{exposure.text}</span> : <span className="text-muted-foreground">not in scope</span>}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Final invoice drafts</CardTitle>
                  <p className="text-xs text-muted-foreground">Brings every draft to what the client still owes, so early payments are never billed again. Drafts also follow new payments on their own every 30 minutes.</p>
                </div>
                <Button size="sm" variant="outline" disabled={syncingDrafts} onClick={async () => {
                  setSyncingDrafts(true);
                  try { setDraftSync(await authedFetch('/api/admin/billing/drafts/sync-all', { method: 'POST' })); }
                  catch (e: any) { setDraftSync({ error: e.message }); } finally { setSyncingDrafts(false); }
                }}>{syncingDrafts ? 'Working…' : 'Bring all drafts to balances'}</Button>
              </div>
            </CardHeader>
            {draftSync && (
              <CardContent className="text-sm">
                {draftSync.error ? <p className="text-destructive">{draftSync.error}</p> : (
                  <p>{draftSync.checked} checked · <b>{draftSync.adjusted} brought to the balance</b> · {draftSync.unchanged} already right{draftSync.not_draft ? ` · ${draftSync.not_draft} already sent (left alone)` : ''}{draftSync.errors ? ` · ${draftSync.errors} could not be checked` : ''}</p>
                )}
              </CardContent>
            )}
          </Card>
          <Card className="border-rose-200">
            <CardHeader className="pb-3 rounded-t-xl bg-rose-50">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base text-rose-900">This morning&apos;s report</CardTitle>
                  {summary?.latest_run && <p className="text-xs text-muted-foreground">{new Date(summary.latest_run.ran_at).toLocaleString('en-US', { timeZone: 'America/Denver', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} MT · {summary.latest_run.trigger}</p>}
                </div>
                <Button size="sm" className="bg-primary text-white hover:bg-primary/90 disabled:opacity-60" disabled={runningReport} onClick={async () => {
                  setRunningReport(true);
                  try {
                    await authedFetch('/api/admin/billing/report/run', { method: 'POST' });
                    setSummary(await authedFetch('/api/admin/billing/summary'));
                    setSummaryError(null);
                  } catch (e: any) { setSummaryError(e.message); } finally { setRunningReport(false); }
                }}>{runningReport ? 'Running…' : 'Run now'}</Button>
              </div>
            </CardHeader>
            <CardContent className="text-sm">
              {summaryError ? <p className="text-destructive">{summaryError}</p>
                : !summary ? <p className="text-muted-foreground">Loading…</p>
                : !summary.latest_run ? <p className="text-muted-foreground">No report recorded yet. The first runs at 6:15 AM Mountain.</p>
                : (() => {
                  const b = summary.latest_run.balances || {};
                  const rc = summary.latest_run.reconcile?.summary || null;
                  const au = summary.latest_run.audit || null;
                  const auditLabel: Record<string, string> = { stored_remaining_vs_ledger: 'stored balance differs from ledger', stored_paid_vs_ledger: 'stored paid total differs from ledger', paid_amendment_without_payment_row: 'amendment paid, no payment row', amendment_addons_vs_charge: 'amendment add-ons do not match charge' };
                  return (
                    <dl className="space-y-3">
                      <ReportRow label="Balances that differ" value={`${b.drift ?? 0} of ${b.active ?? 0}`} note={`${b.drift_lower ?? 0} lower · ${b.drift_higher ?? 0} higher than the stored copy`} />
                      <ReportRow label="Overdue" value={`${b.overdue ?? 0}`} note={`${dollars(b.overdue_cents)} open, past due`} />
                      {b.needs_backfill > 0 && <ReportRow label="Paid in Stripe, unrecorded" value={`${b.needs_backfill}`} note="reconcile backfills these" />}
                      <ReportRow label="Stripe vs site" value={rc ? `${rc.refunds?.to_insert || 0} refund(s) missing` : 'not run'} note={rc ? `${rc.stale_drafts?.still_draft || 0} stale drafts · ${rc.plan_plumbing?.candidates || 0} missing a plan · ${rc.guest_charges?.unmatched || 0} unmatched charges` : (summary.latest_run.notes || '')} warn={!rc} />
                      {au && (
                        <ReportRow label="Ledger audit" value={`${au.total} finding(s)`} note={Object.entries(au.by_check || {}).map(([k, v]) => `${v} × ${auditLabel[k] || k.replace(/_/g, ' ')}`).join(' · ')} />
                      )}
                    </dl>
                  );
                })()}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3 rounded-t-xl bg-slate-50"><CardTitle className="text-base text-slate-900">Stripe events, last 24 hours</CardTitle></CardHeader>
            <CardContent className="text-sm">
              {!summary ? <p className="text-muted-foreground">Loading…</p> : (
                <dl className="space-y-3">
                  <ReportRow label="Processed" value={`${summary.queue.last_24h.processed || 0}`} />
                  <ReportRow label="Failed, retrying" value={`${(summary.queue.last_24h.failed || 0)}`} note={summary.queue.failed_awaiting_retry ? `${summary.queue.failed_awaiting_retry} waiting for a retry` : undefined} warn={(summary.queue.last_24h.failed || 0) > 0} />
                  <ReportRow label="Dead, need a look" value={`${summary.queue.dead_total}`} warn={summary.queue.dead_total > 0} />
                  {summary.queue.problems?.length > 0 ? (
                    <div className="border-t pt-2 space-y-2">
                      {summary.queue.problems.slice(0, 6).map((p: any) => (
                        <div key={p.event_id} className="text-xs flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900">{p.event_type.replace(/[._]/g, ' ')} · {new Date(p.received_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · attempt {p.attempts}</div>
                            <div className="text-slate-600 break-words">{p.error}</div>
                          </div>
                          <Button size="sm" variant="outline" className="h-7 shrink-0 border-slate-300 text-slate-800 hover:bg-slate-50" disabled={requeueing === p.event_id} onClick={async () => {
                            if (!window.confirm(`Run this ${p.event_type} event again now? Do this after the cause of the failure is fixed.`)) return;
                            setRequeueing(p.event_id);
                            try {
                              await authedFetch(`/api/admin/billing/events/${p.event_id}/requeue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: 'Requeued from the billing workbench' }) });
                              toast.success('Requeued. The worker picks it up within seconds; refresh in a minute.');
                              setSummary((s: any) => s ? { ...s, queue: { ...s.queue, problems: s.queue.problems.filter((x: any) => x.event_id !== p.event_id) } } : s);
                            } catch (e: any) { toast.error(e.message || 'Requeue failed'); } finally { setRequeueing(null); }
                          }}>{requeueing === p.event_id ? '…' : 'Requeue'}</Button>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-muted-foreground border-t pt-2">Nothing failed or dead. Every event since the queue went live has processed.</p>}
                </dl>
              )}
            </CardContent>
          </Card>
          <p className="text-xs text-slate-600">Open any row to act on it: create or finalize an invoice, write off, refund, void, or delete a draft. Every action asks for a reason and writes an audit row.</p>
        </div>
      </div>
    </div>
  );
};

const ReportRow = ({ label, value, note, warn }: { label: string; value: string; note?: string; warn?: boolean }) => (
  <div>
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-slate-600">{label}</dt>
      <dd className={`tabular-nums font-semibold ${warn ? 'text-amber-700' : 'text-slate-900'}`}>{value}</dd>
    </div>
    {note && <div className={`text-xs ${warn ? 'text-amber-700' : 'text-slate-500'}`}>{note}</div>}
  </div>
);

export default BillingWorkbench;
